import { Stack } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { IntegerConfigField } from '@/features/controller/configuration/components/drawers/IntegerConfigField'
import { ToggleButtonGroupEnumConfigField } from '@/features/controller/configuration/components/drawers/ToggleButtonGroupEnumConfigField'
import { CONFIG_FIELD_NUMBER_SMALL_SX } from '@/features/controller/configuration/constants'
import { SerialPort, SerialPortParity } from '@/features/controller/configuration/models/raspiProperties/types'
import { describeSerialPort } from '@/features/controller/configuration/utils/serialPorts'

const COMMON_BAUD_RATES = [9600, 19200, 38400, 57600, 115200]

interface SerialPortConfigFieldsProps {
  /** Path of the `SerialPortConfig` in the form, e.g. `legacySerialPortConfig.portConfig`. */
  portConfigFieldName: string
  /** Path of the `SerialPort` in the form, e.g. `legacySerialPortConfig.serialPort`. */
  serialPortFieldName: string
}

/**
 * The serial port and framing fields, shared by the legacy serial drawer and a named serial
 * connection so the two look identical where they overlap. Data bits stays fixed at 8 and disabled
 * in both: RTU requires 8, and the 7-bit modes belong to Modbus ASCII, which neither path produces.
 */
export function SerialPortConfigFields({
  portConfigFieldName,
  serialPortFieldName,
}: Readonly<SerialPortConfigFieldsProps>) {
  const { t } = useTranslation()
  const form = useFormContext()
  const stopBitsFieldName = `${portConfigFieldName}.stopBits`

  return (
    <>
      <ToggleButtonGroupEnumConfigField
        enumLabels={{
          port1: describeSerialPort(SerialPort.port1, t),
          port2: describeSerialPort(SerialPort.port2, t),
        }}
        enumType={SerialPort}
        label={t('controllers.configuration.device_connections.serial.dialog.port')}
        name={serialPortFieldName}
      />
      <datalist id="common_baud_rates">
        {COMMON_BAUD_RATES.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
      <IntegerConfigField
        label={t('controllers.configuration.device_connections.serial.dialog.baud_rate')}
        name={`${portConfigFieldName}.baudRate`}
        textFieldProps={{
          slotProps: { htmlInput: { min: 0, step: 9600, list: 'common_baud_rates' } },
        }}
        unit={t('controllers.configuration.device_connections.serial.baud_rate_unit')}
      />
      <Stack flexDirection="row" gap={2}>
        <IntegerConfigField
          label={t('controllers.configuration.device_connections.serial.dialog.data_bits')}
          name={`${portConfigFieldName}.dataBits`}
          textFieldProps={{ disabled: true, sx: CONFIG_FIELD_NUMBER_SMALL_SX, type: 'number' }}
        />
        <ToggleButtonGroupEnumConfigField
          enumLabels={{
            none: t('controllers.configuration.device_connections.serial.dialog.parity_none'),
            even: t('controllers.configuration.device_connections.serial.dialog.parity_even'),
            odd: t('controllers.configuration.device_connections.serial.dialog.parity_odd'),
          }}
          enumType={SerialPortParity}
          label={t('controllers.configuration.device_connections.serial.dialog.parity')}
          name={`${portConfigFieldName}.parity`}
          toggleButtonGroupControllerProps={{ sx: { height: '42px' } }}
        />
        <ToggleButtonGroupEnumConfigField
          enumLabels={{ one: '1', two: '2' }}
          enumType={{ one: '1', two: '2' }}
          label={t('controllers.configuration.device_connections.serial.dialog.stop_bits')}
          name={stopBitsFieldName}
          toggleButtonGroupControllerProps={{
            sx: { height: '42px' },
            value: String(form.watch(stopBitsFieldName)),
            onChange: (event: any) => {
              form.setValue(stopBitsFieldName, Number(event.target.value), { shouldDirty: true })
            },
          }}
        />
      </Stack>
    </>
  )
}
